import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import {
  FileVideo, Box, Activity, UserCircle, Hand, Footprints,
  RotateCw, Check, ScanLine, Database, Play, XCircle,
  Download, Camera, FileText, Cloud, RotateCcw,
  CheckCircle, ChevronRight, BrainCircuit, Scan, ShieldCheck,
  Info, Loader2, History, User as UserIcon, StickyNote, Save,
  ChevronDown, ChevronUp, CircleDot, Circle, Settings, CloudOff,
  Send, Package, Maximize2, Search, Grid3X3, Calculator,
  Link2, Sparkles, Gauge, CheckCircle2, AlertCircle,
  FileDown as FileDownload, X, Home, Layers
} from "lucide-react";
import { getBaseUrl } from "../config/apiConfig";
import JSZip from "jszip";
import { useWebSocket3D } from "../utils/useWebSocket3D";
import { LoginContext } from "../LoginContext";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import apiService from "../backend/api";
import { getPythonBaseUrl } from "../config/apiConfig";
import FeedbackForm from "../components/FeedbackForm";
import CreditDisplay from "../components/CreditDisplay";
import { saveAs } from "file-saver";

const CATEGORY_AVATARS = {
  default: Box,
  face: UserIcon,
  hand: Hand,
  arm: Activity,
  torso: UserIcon,
  leg: Activity,
  foot: Footprints,
  "1rot": RotateCw,
  "2rot": RotateCw,
  "3rot": RotateCw,
};

const CATEGORY_LABELS = {
  default: "upload.categoryDefault",
  face: "upload.categoryFace",
  hand: "upload.categoryHand",
  arm: "upload.categoryArm",
  torso: "upload.categoryTorso",
  leg: "upload.categoryLeg",
  foot: "upload.categoryFoot",
  "1rot": "upload.category1Rot",
  "2rot": "upload.category2Rot",
  "3rot": "upload.category3Rot",
};

// =============== SUB-COMPONENTS ===============

const SidebarSection = ({ title, children }) => (
  <div className="mb-6 last:mb-0">
    <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-3 px-1">{title}</h3>
    <div className="space-y-2 px-1">{children}</div>
  </div>
);

const MetadataItem = ({ label, value }) => (
  <div className="flex flex-col">
    <span className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wider leading-none mb-1">{label}</span>
    <span className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate" title={value}>{value ?? "---"}</span>
  </div>
);

const ControlGroup = ({ title, children }) => (
  <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg lg:rounded-xl p-2 md:p-4 flex-1 min-w-0 md:min-w-fit shadow-sm">
    <h4 className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1.5 md:mb-4">{title}</h4>
    <div className="flex items-center gap-2 md:gap-3 lg:space-x-6 flex-wrap">
      {children}
    </div>
  </div>
);

const IconButton = ({ icon: Icon, label, onClick, disabled, variant = "ghost" }) => {
  const baseStyle = "flex flex-col items-center justify-center space-y-1 md:space-y-2 transition-all duration-200 group";
  const iconStyle = `w-10 h-10 md:w-11 md:h-11 rounded-lg flex items-center justify-center border ${
    variant === "primary"
      ? "bg-[#0f7699] dark:bg-[#4fc3f7] border-[#0f7699] dark:border-[#4fc3f7] text-white hover:bg-[#0f7699]/90 dark:hover:bg-[#4fc3f7]/90"
      : "bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 group-hover:border-[#0f7699] dark:group-hover:border-[#4fc3f7] group-hover:text-[#0f7699] dark:group-hover:text-[#4fc3f7]"
  } ${disabled ? "opacity-30 cursor-not-allowed" : "cursor-pointer active:scale-95"}`;

  return (
    <button onClick={onClick} disabled={disabled} className={baseStyle}>
      <div className={iconStyle}>
        <Icon size={20} />
      </div>
      <span className="text-[10px] font-medium text-slate-500 dark:text-slate-400 group-hover:text-[#0f7699] dark:group-hover:text-[#4fc3f7] transition-colors">{label}</span>
    </button>
  );
};

// =============== WORKFLOW STEP COMPONENT ===============
const WorkflowStep = ({ step, index, isCompleted, isActive, isFailed, statusText }) => {
  return (
    <div className="relative flex items-center group">
      {/* Step indicator circle */}
      <div className={`shrink-0 w-10 h-10 rounded-full border-2 flex items-center justify-center z-10 transition-all duration-300 ${
        isFailed
          ? "bg-red-100 dark:bg-red-900/30 border-red-500 text-red-600 dark:text-red-400"
          : isCompleted
            ? "bg-[#0f7699]/10 dark:bg-[#4fc3f7]/15 border-[#0f7699] dark:border-[#4fc3f7] text-[#0f7699] dark:text-[#4fc3f7]"
            : isActive
              ? "bg-[#0f7699]/5 dark:bg-[#4fc3f7]/10 border-[#0f7699] dark:border-[#4fc3f7] text-[#0f7699] dark:text-[#4fc3f7] ring-4 ring-[#0f7699]/20 dark:ring-[#4fc3f7]/20"
              : "bg-slate-50 dark:bg-slate-900 border-slate-300 dark:border-slate-700 text-slate-400 dark:text-slate-600"
      }`}>
        {isFailed ? (
          <XCircle size={18} />
        ) : isCompleted ? (
          <Check size={18} />
        ) : isActive ? (
          <CircleDot size={18} className="animate-pulse" />
        ) : (
          <span className="text-xs font-semibold">{index + 1}</span>
        )}
      </div>

      {/* Step label */}
      <div className="ml-4 lg:ml-5 whitespace-nowrap lg:whitespace-normal">
        <p className={`text-sm font-medium transition-colors duration-200 ${
          isFailed
            ? "text-red-600 dark:text-red-400"
            : isActive
              ? "text-slate-900 dark:text-white"
              : isCompleted
                ? "text-[#0f7699] dark:text-[#4fc3f7]"
                : "text-slate-400 dark:text-slate-600"
        }`}>
          {step.translatedLabel}
        </p>
        <span className={`text-xs ${
          isFailed
            ? "text-red-500 dark:text-red-400"
            : isActive
              ? "text-[#0f7699] dark:text-[#4fc3f7] font-medium"
              : isCompleted
                ? "text-[#0f7699] dark:text-[#4fc3f7] dark:text-[#4fc3f7]"
                : "text-slate-400 dark:text-slate-700"
        }`}>
          {statusText}
        </span>
      </div>
    </div>
  );
};

// Get micro-stage for photogrammetry phase based on percentage (generic names that don't reveal process)
const getPhotogrammetryMicroStage = (percentage) => {
  if (percentage < 15) return { label: "upload.processingAnalyze", icon: "Search" };
  if (percentage < 25) return { label: "upload.processingStructure", icon: "Grid3X3" };
  if (percentage < 35) return { label: "upload.processingCalculate", icon: "Calculator" };
  if (percentage < 45) return { label: "upload.processingLink", icon: "Link2" };
  if (percentage < 55) return { label: "upload.processingModel", icon: "Box" };
  if (percentage < 65) return { label: "upload.processingRefine", icon: "Sparkles" };
  if (percentage < 75) return { label: "upload.processingOptimize", icon: "Gauge" };
  if (percentage < 85) return { label: "upload.processingFinalize", icon: "CheckCircle2" };
  return { label: "upload.processingProcess", icon: "Loader2" };
};

// Get processing stage label based on log messages (defined outside component to avoid recreation)
const getProcessingStageLabel = (stage, percentage) => {
  // First check for log-based stages
  switch (stage) {
    case "vorbereiten":
      return "upload.processingPrepare";
    case "photogrammetrie":
      return getPhotogrammetryMicroStage(percentage).label;
    case "abschliessen":
      return "upload.processingComplete";
    case "skalieren":
      return "upload.processingScale";
    case "cloud-upload":
      return "upload.processingCloudUpload";
    default:
      // Fallback to percentage-based labels for photogrammetry phases
      if (percentage > 0 && percentage < 92) {
        return getPhotogrammetryMicroStage(percentage).label;
      }
      return "upload.processingRunning";
  }
};

// Get processing stage for backend log persistence (percentage-based fallback)
const getProcessingStage = (percentage) => {
  if (percentage <= 0) return null;
  if (percentage < 10) return "upload.stagePrepare";
  if (percentage < 85) return "upload.stagePhotogrammetry";
  if (percentage < 92) return "upload.stageComplete";
  if (percentage < 100) return "upload.stageScale";
  return "upload.stageFinished";
};

export default function UploadPage() {
  const { t } = useTranslation();
  const ws = useWebSocket3D();
  const videoInputRef = useRef();
  const abortRequestedRef = useRef(false);
  const highestStatusIndexRef = useRef(-1); // Track highest status index to prevent backwards status during history replay
  const highestVisualStepRef = useRef(-1); // Track highest UI step index so transitions between inputs can't flash backward to step 0
  const isStartingProcessRef = useRef(false); // Prevent concurrent startProcess() calls
  const location = useLocation();

  const [steps] = useState([
    { id: "S1", labelKey: "upload.stepSelectVideo" },
    { id: "S2", labelKey: "upload.stepAnalyzeVideo" },
    { id: "S3", labelKey: "upload.stepUpload" },
    { id: "S4", labelKey: "upload.stepCreate3DModel" },
    { id: "S5", labelKey: "upload.stepCheckResult" }
  ]);

  const [categories] = useState([
    { value: "default", labelKey: "upload.categoryDefault", frames: 100 },
    { value: "face", labelKey: "upload.categoryFace", frames: 200 },
    { value: "hand", labelKey: "upload.categoryHand", frames: 100 },
    { value: "arm", labelKey: "upload.categoryArm", frames: 100 },
    { value: "torso", labelKey: "upload.categoryTorso", frames: 100 },
    { value: "leg", labelKey: "upload.categoryLeg", frames: 100 },
    { value: "foot", labelKey: "upload.categoryFoot", frames: 200 },
    { value: "1rot", labelKey: "upload.category1Rot", frames: 100 },
    { value: "2rot", labelKey: "upload.category2Rot", frames: 200 },
    { value: "3rot", labelKey: "upload.category3Rot", frames: 300 },
  ]);

  const { loginInfo, logout } = useContext(LoginContext);
  const navigate = useNavigate();
  const currentUser = loginInfo?.user || { username: "ANONYMOUS", roles: [] };
  const isAdmin = currentUser.roles?.some(r => typeof r === 'string' ? r === 'admin' : r.name === 'admin');

  const [selectedCategory, setSelectedCategory] = useState("default");
  const [showCategorySheet, setShowCategorySheet] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [extractionProgress, setExtractionProgress] = useState(0);
  const [isExtracting, setIsExtracting] = useState(false);
  const [isUplinking, setIsUplinking] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [systemLogs, setSystemLogs] = useState([]);
  const [serverLogs, setServerLogs] = useState([]);
  const [isPreviewAccepted, setIsPreviewAccepted] = useState(false);
  const [showLogsModal, setShowLogsModal] = useState(false);

  const [statusInfo, setStatusInfo] = useState({
    jobId: null,
    pythonJobId: null,
    category: null,
    preview_url: null,
    cloud_url: null,
    jobProgress: 0,
    workerNum: 0,
    queueLength: 0,
    queuePosition: null,
    restoredStatus: null,
    isQueued: false, // True if scan is waiting behind another user's scan
  });

  // Credit states - differenziert nach Typ
  const [credits, setCredits] = useState({
    subscription: 0,
    subscriptionTotal: 0, // Gesamt-Abo-Credits im aktuellen Zeitraum
    subscriptionUsed: 0,  // Verbrauchte Abo-Credits im aktuellen Zeitraum
    bonus: 0,
    package: 0,
    total: null, // null = noch nicht geladen
    nextBillingDate: null, // Nächstes Abrechnungsdatum
  });
  // Free scan attempt limits (null = not loaded, { unlimited: true } for paid users)
  const [scanLimits, setScanLimits] = useState(null);

  const [isConsuming, setIsConsuming] = useState(false);
  const [isRestoringSession, setIsRestoringSession] = useState(false);
  const [hasActiveSession, setHasActiveSession] = useState(false);
  const [isAccepting, setIsAccepting] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [isCurrentTrialScan, setIsCurrentTrialScan] = useState(false);

  // Download format modal states
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [downloadFormat, setDownloadFormat] = useState("OBJ+PNG");
  const [isDownloading, setIsDownloading] = useState(false);
  const iframeRef = useRef(null);
  const [iframeMeshLabel, setIframeMeshLabel] = useState("Mesh");
  const iframeSrc = useMemo(() => {
    if (!statusInfo.preview_url) return null;
    const sep = statusInfo.preview_url.includes('?') ? '&' : '?';
    const mobile = window.innerWidth < 1024 ? '&mobile=1' : '';
    return `${statusInfo.preview_url}${sep}_v=${Date.now()}${mobile}`;
  }, [statusInfo.preview_url]);
  const downloadTokenRef = useRef(null);
  const projectTitleRef = useRef(null);

  // Project notes state
  const [nextcloudStatus, setNextcloudStatus] = useState(null);
  const [showProjectNotes, setShowProjectNotes] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);
  const [projectNotes, setProjectNotes] = useState({
    bodyRegion: "",
    indication: "",
    clinicalFindings: "",
    diagnosis: "",
    treatmentRecommendations: "",
    followUpNotes: "",
    scalingFactor: "",
    qualityNotes: "",
    exportFormats: "",
    caseNumber: "",
    department: "",
    referringPhysician: "",
    processingStatus: "",
    additionalNotes: "",
  });

  // Toast state
  const [toast, setToast] = useState({ open: false, message: "", severity: "info" });
  const showToast = (message, severity = "info") => setToast({ open: true, message, severity });
  const handleCloseToast = () => setToast(prev => ({ ...prev, open: false }));

  // Auto-dismiss toast after 4 seconds
  useEffect(() => {
    if (!toast.open) return;
    const timer = setTimeout(() => handleCloseToast(), 4000);
    return () => clearTimeout(timer);
  }, [toast.open, toast.message]);

  const isConnected = ws.connected;

  // Effective status calculation - always use highest status seen to prevent backwards progress during history replay
  const effectiveStatus = useMemo(() => {
    const statusOrder = ['created', 'file_pending', 'file_received', 'queued', 'in_progress', 'processing', 'review', 'ready', 'completed'];
    const wsIndex = statusOrder.indexOf(ws.jobStatus);
    const restoredIndex = statusOrder.indexOf(statusInfo.restoredStatus);

    // Get the highest index between WS and restored status
    const currentHighestIndex = Math.max(wsIndex, restoredIndex);

    // Only update if we've progressed forward (prevents backwards during history replay)
    if (currentHighestIndex > highestStatusIndexRef.current) {
      highestStatusIndexRef.current = currentHighestIndex;
    }

    // Return the status at the highest index we've seen
    if (highestStatusIndexRef.current >= 0) {
      return statusOrder[highestStatusIndexRef.current];
    }

    // Fallback to any available status
    return ws.jobStatus || statusInfo.restoredStatus;
  }, [ws.jobStatus, statusInfo.restoredStatus]);

  const visualStep = useMemo(() => {
    // Compute the step that the *current* inputs would suggest. The order
    // mirrors the original cascade but the result is then clamped against
    // `highestVisualStepRef` below so transient gaps between inputs (e.g.
    // isExtracting flips false before uploadProgress > 0, or ws.jobStatus is
    // briefly empty on reconnect) don't cause a backward flash to step 0.
    let computed;
    if (effectiveStatus === "failed") computed = -1;
    else if (isPreviewAccepted) computed = 5;
    else if (effectiveStatus === "completed" || effectiveStatus === "review" || effectiveStatus === "ready") computed = 4;
    else {
      const synthesisStatus = ["queued", "in_progress", "processing"];
      if (synthesisStatus.includes(effectiveStatus)) computed = 3;
      else if (isExtracting) computed = 1;
      else {
        const uplinkStatus = ["created", "file_pending", "file_received"];
        if (isUplinking || (uploadProgress > 0 && uploadProgress < 100) || uplinkStatus.includes(effectiveStatus)) computed = 2;
        else computed = 0;
      }
    }

    // "failed" and "preview accepted" override the monotonic tracker:
    //  -1 (failed) must be allowed to surface regardless of prior progress.
    //   5 (accepted) is the explicit terminal state.
    if (computed === -1 || computed === 5) {
      return computed;
    }

    // Otherwise: never go backwards. If the inputs momentarily suggest a
    // lower step than what we've previously displayed, hold the higher one.
    if (computed > highestVisualStepRef.current) {
      highestVisualStepRef.current = computed;
    }
    return highestVisualStepRef.current >= 0 ? highestVisualStepRef.current : computed;
  }, [effectiveStatus, uploadProgress, isExtracting, isUplinking, isPreviewAccepted]);

  // Get status text for each step
  const getStepStatus = (idx) => {
    const isFailed = ws.jobStatus === "failed" && idx === 3;
    const isCompleted = visualStep > idx;
    const isActive = visualStep === idx;

    if (isFailed) return t("upload.stepError");
    // Show "Abgeschlossen" first if step is completed, before showing percentages
    if (isCompleted) return t("upload.stepCompleted");
    if (idx === 1 && isExtracting) return `${extractionProgress}%`;
    if (idx === 2 && uploadProgress > 0 && uploadProgress < 100) return `${uploadProgress}%`;
    if (isActive) return t("upload.stepInProgress");
    return t("upload.stepPending");
  };

  // Animated sync stage for acceptance process ONLY (not after accepted)
  const [syncStageIndex, setSyncStageIndex] = useState(0);
  const syncStages = [
    "upload.syncEncrypt",
    "upload.syncConnect",
    "upload.syncData",
    "upload.syncMetadata",
    "upload.syncBackup",
    "upload.syncCheck"
  ];

  useEffect(() => {
    // Only animate DURING acceptance process, not after
    if (!isAccepting) {
      setSyncStageIndex(0);
      return;
    }
    const interval = setInterval(() => {
      setSyncStageIndex(prev => (prev + 1) % syncStages.length);
    }, 2500);
    return () => clearInterval(interval);
  }, [isAccepting, syncStages.length]);

  // Listen for download results from 3D viewer iframe
  useEffect(() => {
    const handleDownloadResult = (event) => {
      if (event.data?.type !== 'download-3d-model-result') return;

      setIsDownloading(false);

      if (event.data.success) {
        try {
          const binaryString = atob(event.data.data);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          const blob = new Blob([bytes], { type: event.data.mimeType });
          saveAs(blob, event.data.filename);
          addSystemLog(t("upload.downloadSuccess"), "success");
          showToast(t("upload.downloadSuccess") + "!", "success");
        } catch (err) {
          console.error('Failed to process download data:', err);
          showToast(t("upload.downloadFailed"), "error");
        }
      } else {
        console.error('Export failed:', event.data.error);
        showToast("Export fehlgeschlagen: " + (event.data.error || ''), "error");
      }
    };

    window.addEventListener('message', handleDownloadResult);
    return () => window.removeEventListener('message', handleDownloadResult);
  }, []);

  // =============== LOGGING ===============
  const addSystemLog = useCallback((message, type = "info") => {
    const log = { id: Date.now(), message, type, time: new Date().toLocaleTimeString() };
    setSystemLogs(prev => [log, ...prev].slice(0, 5));
  }, []);

  const addServerLog = useCallback((message, type = "info") => {
    const log = { message, type, time: new Date().toLocaleTimeString() };
    setServerLogs(prev => [...prev, log]);
  }, []);

  // =============== WEBSOCKET EFFECTS ===============
  const lastSyncedStatusRef = useRef(null);
  const currentJobIdRef = useRef(null); // Synchronous job ID tracker to prevent race conditions
  const lastLogSaveRef = useRef(0); // Debounce log saves
  const pendingLogsRef = useRef([]); // Buffer for logs to save

  useEffect(() => {
    if (!ws.lastMessage || abortRequestedRef.current) return;
    const msg = ws.lastMessage;
    if (msg.message) addServerLog(msg.message, msg.status_type);

    if (msg.content) {
      const c = msg.content;
      const incomingJobId = c.job_id || ws.jobId;

      // ✅ FIX: Only process messages for OUR current job (prevents parallel scan conflicts)
      // Normalize job IDs by removing trailing X suffix(es) for comparison
      const normalizeJobId = (id) => id?.replace(/X+$/, '') || '';
      // Extract the timestamp+random suffix so we can match across category rename
      // (e.g. `default_1779136068004_uyhadc` and `hand_1779136068004_uyhadc` are
      //  the SAME job — Python renames the temp `default_*` id to `<category>_*`
      //  via get_job_id at client_connection.py:253). Without this, the rename
      //  causes every subsequent WS message to be filtered out so no upload /
      //  photogrammetry progress reaches the UI.
      const jobIdSuffix = (id) => {
        const cleaned = normalizeJobId(id);
        const m = cleaned.match(/_(\d+)_([a-z0-9]+)$/);
        return m ? `${m[1]}_${m[2]}` : cleaned;
      };

      // Check against ALL known job IDs (jobId, pythonJobId, currentJobIdRef)
      // This ensures resumed projects aren't filtered out
      const ourJobIds = [
        currentJobIdRef.current,
        statusInfo.jobId,
        statusInfo.pythonJobId
      ].filter(Boolean);

      if (ourJobIds.length > 0 && incomingJobId) {
        // Exception: Always accept messages from temporary session IDs (default_*) during upload phase
        if (!incomingJobId.startsWith('default_')) {
          const incomingNormalized = normalizeJobId(incomingJobId);
          const incomingSuffix = jobIdSuffix(incomingJobId);

          // Match either by full normalized id (resumed jobs) OR by the
          // <timestamp>_<random> suffix (handles the default_→<category>_ rename).
          const matchesOurJob = ourJobIds.some(ourId => {
            if (normalizeJobId(ourId) === incomingNormalized) return true;
            if (incomingSuffix && jobIdSuffix(ourId) === incomingSuffix) return true;
            return false;
          });

          if (!matchesOurJob) {
            console.log(`⚠️ [WS] Ignoring message for different job: ${incomingJobId} (our jobs: ${ourJobIds.join(', ')})`);
            return;
          }
        }
      }

      const pythonBackendUrl = getPythonBaseUrl().replace(/\/+$/, '');
      let fullPreviewUrl = c.preview_url;
      let fullCloudUrl = c.cloud_url;

      if (c.cloud_url && c.cloud_url.startsWith('/')) {
        fullCloudUrl = `${pythonBackendUrl}${c.cloud_url}`;
      }
      if (c.preview_url && c.preview_url.startsWith('/')) {
        fullPreviewUrl = `${pythonBackendUrl}${c.preview_url}`;
      }

      // Auto-generate preview_url when status reaches review and we have job info
      const currentJobId = ws.jobId || c.job_id || statusInfo.pythonJobId || statusInfo.jobId;
      const currentCategory = c.category || statusInfo.category || selectedCategory;
      let autoPreviewUrl = fullPreviewUrl;
      let autoCloudUrl = fullCloudUrl;

      if (!autoPreviewUrl && currentJobId && currentCategory && (c.job_status === 'review' || c.job_status === 'completed')) {
        autoPreviewUrl = `${pythonBackendUrl}/mesh-viewer?subpath=${currentCategory}/${currentJobId}`;
      }
      if (!autoCloudUrl && currentJobId && currentCategory && (c.job_status === 'review' || c.job_status === 'completed')) {
        autoCloudUrl = `${pythonBackendUrl}/file-explorer/${currentCategory}/${currentJobId}/`;
      }

      setStatusInfo(prev => ({
        ...prev,
        pythonJobId: ws.jobId || c.job_id || prev.pythonJobId,
        category: c.category || prev.category,
        jobProgress: c.job_percentage ?? prev.jobProgress,
        queuePosition: c.queue_position ?? prev.queuePosition,
        preview_url: autoPreviewUrl || prev.preview_url,
        cloud_url: autoCloudUrl || prev.cloud_url,
        workerNum: c.worker_num ?? prev.workerNum,
        clientNum: c.client_num ?? prev.clientNum,
        queueLength: c.queue_length ?? prev.queueLength,
      }));

      if (c.upload_percentage != null) setUploadProgress(Math.round(c.upload_percentage));

      const nodeJobId = statusInfo.jobId;
      const pythonJobId = c.job_id || ws.jobId;
      const currentStatus = c.job_status || ws.jobStatus;

      if (nodeJobId && currentStatus && typeof apiService.updateProjectStatus === "function") {
        const syncKey = `${nodeJobId}-${currentStatus}`;
        if (lastSyncedStatusRef.current === syncKey) {
          return;
        }
        lastSyncedStatusRef.current = syncKey;

        const previewUrl = fullPreviewUrl || c.preview_url || statusInfo.preview_url;
        const cloudUrlForSync = fullCloudUrl || c.cloud_url || statusInfo.cloud_url;
        const categoryForSync = c.category || statusInfo.category || selectedCategory;

        apiService.updateProjectStatus(nodeJobId, currentStatus, previewUrl, currentStatus, pythonJobId, categoryForSync, cloudUrlForSync)
          .then((result) => {
            if (result?.renamed && result?.project?.jobId) {
              setStatusInfo(prev => ({
                ...prev,
                jobId: result.project.jobId,
                pythonJobId: result.project.jobId,
              }));
            }

            if (currentStatus === "completed") {
              addSystemLog("Projekt erfolgreich synchronisiert.", "success");
            }
          })
          .catch(err => {
            console.error("Sync-Fehler:", err);
            lastSyncedStatusRef.current = null;
            if (currentStatus === "completed") {
              addSystemLog("Synchronisation fehlgeschlagen", "error");
            }
          });
      }
    }
  }, [ws.lastMessage, ws.jobId, ws.jobStatus, addServerLog, statusInfo.jobId, statusInfo.preview_url, statusInfo.cloud_url, statusInfo.category, selectedCategory]);

  useEffect(() => {
    const nodeJobId = statusInfo.jobId;
    const pythonJobId = statusInfo.pythonJobId || ws.jobId;
    const currentStatus = ws.jobStatus;

    if (!nodeJobId || !currentStatus || abortRequestedRef.current) return;

    const syncableStatuses = ['created', 'file_pending', 'file_received', 'queued', 'in_progress', 'processing', 'review', 'completed', 'failed', 'aborted'];
    if (!syncableStatuses.includes(currentStatus)) return;

    const syncKey = `${nodeJobId}-${currentStatus}`;
    if (lastSyncedStatusRef.current === syncKey) return;
    lastSyncedStatusRef.current = syncKey;

    const categoryForBackup = statusInfo.category || selectedCategory;
    const cloudUrlForBackup = statusInfo.cloud_url;

    apiService.updateProjectStatus(nodeJobId, currentStatus, statusInfo.preview_url, currentStatus, pythonJobId, categoryForBackup, cloudUrlForBackup)
      .then((result) => {
        if (result?.renamed && result?.project?.jobId) {
          setStatusInfo(prev => ({
            ...prev,
            jobId: result.project.jobId,
            pythonJobId: result.project.jobId,
          }));
        }
      })
      .catch(err => {
        console.error("Backup-Sync-Fehler:", err);
        lastSyncedStatusRef.current = null;
      });
  }, [ws.jobStatus, ws.jobId, statusInfo.jobId, statusInfo.pythonJobId, statusInfo.preview_url, statusInfo.category, selectedCategory]);

  // =============== SAVE LOGS TO BACKEND (for session restoration) ===============
  useEffect(() => {
    const jobId = statusInfo.jobId;
    const progress = statusInfo.jobProgress;

    if (!jobId || !progress || progress === 0) return;

    // Debounce: Save at most every 5 seconds
    const now = Date.now();
    if (now - lastLogSaveRef.current < 5000) return;
    lastLogSaveRef.current = now;

    // Use the existing getProcessingStage function defined above
    const progressStage = getProcessingStage(progress);

    // Save progress to backend (logs are optional, mainly for progress tracking)
    apiService.saveWorkerLogs(jobId, [], progress, progressStage)
      .catch(err => console.warn("Log save failed:", err.message));

  }, [statusInfo.jobId, statusInfo.jobProgress]);

  const fetchDownloadBalance = useCallback(async () => {
    if (!currentUser.username || currentUser.username === "ANONYMOUS") return;
    try {
      const data = await apiService.getUserSubscriptions(currentUser.username);
      const sub = data.subscription;
      const subCredits = sub?.remainingDownloads || sub?.downloadsRemaining || 0;

      // Plan-basierter Lookup für Downloads pro Monat (dynamisch aus DB)
      const planName = sub?.planName || "Free";
      const planStatus = sub?.status || "inaktiv";
      let planBasedDownloads = 0;
      try {
        const plans = await apiService.request(`${getBaseUrl()}/api/payment/plans`);
        const matchedPlan = plans.find(p => p.name === planName && p.type === 'subscription');
        planBasedDownloads = matchedPlan?.downloads || 0;
      } catch { planBasedDownloads = 0; }

      // Nutze downloadsPerMonth, aber falls es kleiner ist als remainingDownloads oder
      // bei "wechselnd" Status (wo der alte Plan noch gilt), nutze den Plan-Lookup
      const rawDownloadsPerMonth = sub?.downloadsPerMonth || 0;
      const subTotal = (rawDownloadsPerMonth < subCredits || planStatus === "wechselnd")
        ? planBasedDownloads
        : rawDownloadsPerMonth;

      const subUsed = Math.max(0, subTotal - subCredits);
      const bonusCredits = data.bonusDownloads || 0;
      const pkgCredits = data.packageDownloads || 0;
      const total = subCredits + bonusCredits + pkgCredits;
      // Get next billing date from subscription
      const billingDate = sub?.currentPeriodEnd
        ? new Date(sub.currentPeriodEnd * 1000).toLocaleDateString('de-DE')
        : (sub?.nextBillingDate ? new Date(sub.nextBillingDate).toLocaleDateString('de-DE') : null);
      setCredits({
        subscription: subCredits,
        subscriptionTotal: subTotal,
        subscriptionUsed: subUsed,
        bonus: bonusCredits,
        package: pkgCredits,
        total: total,
        nextBillingDate: billingDate,
      });
    } catch (err) {
      console.error("Fehler beim Laden des Guthabens:", err);
    }
  }, [currentUser.username]);

  useEffect(() => {
    fetchDownloadBalance();
  }, [fetchDownloadBalance]);

  // Fetch free scan attempt limits
  useEffect(() => {
    const fetchScanLimits = async () => {
      if (!currentUser.username || currentUser.username === "ANONYMOUS") return;
      try {
        const data = await apiService.getScanAttemptsRemaining();
        setScanLimits(data);
      } catch (err) {
        console.error("Scan-Limits Fehler:", err);
      }
    };
    fetchScanLimits();
  }, [currentUser.username]);

  useEffect(() => {
    const fetchNextcloudStatus = async () => {
      if (!currentUser.username || currentUser.username === "ANONYMOUS") return;
      try {
        const response = await apiService.request(`${getBaseUrl()}/api/nextcloud/status`);
        setNextcloudStatus(response);
      } catch (err) {
        console.error("Nextcloud-Status Fehler:", err);
        setNextcloudStatus({ connected: false });
      }
    };

    fetchNextcloudStatus();
  }, [currentUser.username]);

  // Screenshot listener
  useEffect(() => {
    const handleScreenshotMessage = async (event) => {
      if (event.data?.type !== '3d-preview-screenshot' || !event.data?.screenshot) {
        return;
      }

      const jobId = statusInfo.jobId || statusInfo.pythonJobId;
      if (!jobId) return;

      try {
        await apiService.request(`${getBaseUrl()}/api/user/projects/${jobId}/preview`, {
          method: 'PUT',
          body: JSON.stringify({ image: event.data.screenshot })
        });
        addSystemLog("Vorschaubild gespeichert", "success");
      } catch (err) {
        console.error('Vorschaubild speichern fehlgeschlagen:', err);
      }
    };

    window.addEventListener('message', handleScreenshotMessage);
    return () => window.removeEventListener('message', handleScreenshotMessage);
  }, [statusInfo.jobId, statusInfo.pythonJobId]);

  // Listen for viewer state updates (mesh label)
  useEffect(() => {
    const handleViewerState = (event) => {
      if (event.data?.type === 'viewer-state' && event.data.meshLabel) {
        setIframeMeshLabel(event.data.meshLabel);
      }
    };
    window.addEventListener('message', handleViewerState);
    return () => window.removeEventListener('message', handleViewerState);
  }, []);

  // Save project notes
  const saveProjectNotes = async () => {
    const jobId = statusInfo.jobId || statusInfo.pythonJobId;
    if (!jobId || !nextcloudStatus?.connected) return;

    setSavingNotes(true);
    try {
      await apiService.request(`${getBaseUrl()}/api/nextcloud/project-notes/${jobId}`, {
        method: 'PUT',
        body: JSON.stringify({ notes: projectNotes })
      });
      showToast("Notizen gespeichert", "success");
      addSystemLog("Notizen in Cloud gespeichert", "success");
    } catch (err) {
      console.error('Notizen speichern fehlgeschlagen:', err);
      showToast("Fehler beim Speichern", "error");
    } finally {
      setSavingNotes(false);
    }
  };

  const updateNote = (field, value) => {
    setProjectNotes(prev => ({ ...prev, [field]: value }));
  };

  // =============== SESSION RESTORATION (only via Projects page) ===============
  useEffect(() => {
    const checkActiveProject = async () => {
      if (!currentUser.username || currentUser.username === "ANONYMOUS") return;

      // Only restore session if explicitly passed from Projects page
      // Auto-restore disabled - user must manually return via Projects page
      const resumeProject = location.state?.resumeProject;
      if (!resumeProject) return;

      try {
        setIsRestoringSession(true);

        // First, fetch the current project status from backend (status might have changed)
        let project = resumeProject;
        try {
          const freshProject = await apiService.getActiveProject();
          if (freshProject && freshProject.jobId === resumeProject.jobId) {
            project = freshProject;
            console.log(`🔄 [SESSION] Fresh status for ${project.jobId}: ${project.status}`);
          }
        } catch (fetchErr) {
          console.log("Using cached project data (fresh fetch failed):", fetchErr.message);
        }

        if (project) {
          const pythonBackendUrl = getPythonBaseUrl().replace(/\/+$/, '');

          let previewUrl = project.previewUrl;
          // If previewUrl exists but is relative, prepend the base URL
          if (previewUrl && !previewUrl.startsWith("http")) {
            previewUrl = pythonBackendUrl + (previewUrl.startsWith('/') ? '' : '/') + previewUrl;
          }
          // If no previewUrl, construct it from category and jobId
          if (!previewUrl && project.category && project.jobId) {
            previewUrl = `${pythonBackendUrl}/mesh-viewer?subpath=${project.category}/${project.jobId}`;
          }

          let cloudUrl = project.cloudUrl;
          // If cloudUrl exists but is relative, prepend the base URL
          if (cloudUrl && !cloudUrl.startsWith("http")) {
            cloudUrl = pythonBackendUrl + (cloudUrl.startsWith('/') ? '' : '/') + cloudUrl;
          }

          // ✅ Load saved progress from backend
          let savedProgress = 0;
          let savedProgressStage = null;
          try {
            const logsData = await apiService.getWorkerLogs(project.jobId);
            if (logsData) {
              savedProgress = logsData.lastProgress || 0;
              savedProgressStage = logsData.lastProgressStage || null;
              console.log(`📊 [SESSION] Restored progress: ${savedProgress}% - ${savedProgressStage}`);

              // If we have saved URLs from logs, use them
              if (logsData.previewUrl && !previewUrl) {
                previewUrl = logsData.previewUrl;
              }
              if (logsData.cloudUrl && !cloudUrl) {
                cloudUrl = logsData.cloudUrl;
              }

              // ✅ Restore worker logs to display (admin only sees these in logs modal)
              if (logsData.workerLogs && logsData.workerLogs.length > 0) {
                const restoredLogs = logsData.workerLogs.map(log => ({
                  message: log.message,
                  type: log.type || 'info',
                  time: log.timestamp ? new Date(log.timestamp).toLocaleTimeString() : '--:--:--'
                }));
                setServerLogs(restoredLogs);
                console.log(`📋 [SESSION] Restored ${restoredLogs.length} worker logs`);
              }
            }
          } catch (logsErr) {
            console.log("Could not load saved progress:", logsErr.message);
          }

          // ✅ Update refs SYNCHRONOUSLY for restored session
          currentJobIdRef.current = project.jobId;
          lastSyncedStatusRef.current = null;

          // Initialize highest status based on restored status
          const statusOrder = ['created', 'file_pending', 'file_received', 'queued', 'in_progress', 'processing', 'review', 'ready', 'completed'];
          const restoredStatusIndex = statusOrder.indexOf(project.status);
          if (restoredStatusIndex > highestStatusIndexRef.current) {
            highestStatusIndexRef.current = restoredStatusIndex;
          }

          // Also seed the visual-step tracker so the first render after restore
          // doesn't briefly show step 0 before useMemo catches up.
          const stepForStatus = (s) => {
            if (s === "completed" || s === "review" || s === "ready") return 4;
            if (s === "queued" || s === "in_progress" || s === "processing") return 3;
            if (s === "created" || s === "file_pending" || s === "file_received") return 2;
            return -1;
          };
          const seededStep = stepForStatus(project.status);
          if (seededStep > highestVisualStepRef.current) {
            highestVisualStepRef.current = seededStep;
          }

          // Determine progress: use saved progress for processing, 100 for review/ready
          const restoredProgress = (project.status === "review" || project.status === "ready")
            ? 100
            : savedProgress > 0 ? savedProgress : 0;

          setStatusInfo(prev => ({
            ...prev,
            jobId: project.jobId,
            pythonJobId: project.jobId,
            category: project.category || prev.category,
            preview_url: previewUrl,
            cloud_url: cloudUrl,
            restoredStatus: project.status,
            jobProgress: restoredProgress,
          }));

          if (project.status === "ready") {
            setIsPreviewAccepted(true);
          }

          // Store project title for download filenames
          if (project.title) {
            projectTitleRef.current = project.title;
          }

          setHasActiveSession(true);
          showToast(`Aktiver Job: ${project.jobId}`, "info");
          addSystemLog(`Session wiederhergestellt: ${project.jobId} (${project.status}${savedProgress > 0 ? `, ${savedProgress}%` : ''})`, "success");

          // Connect WebSocket and wait for connection to establish
          // Pass jobId to connect to the specific session for this job (important for parallel scans)
          if (["queued", "uploading", "processing", "in_progress", "review"].includes(project.status)) {
            if (!ws.connected && project.jobId) {
              try {
                await ws.connect(project.jobId);
                console.log("🔌 [WS] Connected for restored session:", project.jobId);
              } catch (wsErr) {
                console.warn("🔌 [WS] Connection failed for restored session:", wsErr);
              }
            }
          }
        }
      } catch (err) {
        console.error("Session-Wiederherstellung fehlgeschlagen:", err);
      } finally {
        setIsRestoringSession(false);
      }
    };

    checkActiveProject();
  }, [currentUser.username, location.state?.resumeProject]);

  // =============== STATUS POLLING (for restored sessions) ===============
  useEffect(() => {
    // Only poll if we have an active session in processing state
    const activeStatuses = ["queued", "uploading", "processing", "in_progress"];
    const currentStatus = statusInfo.restoredStatus || ws.jobStatus;

    if (!hasActiveSession || !statusInfo.jobId || !activeStatuses.includes(currentStatus)) {
      return;
    }

    const pollForStatus = async () => {
      try {
        const freshProject = await apiService.getActiveProject();
        if (freshProject && freshProject.jobId === statusInfo.jobId) {
          const newStatus = freshProject.status;
          const statusChanged = newStatus !== statusInfo.restoredStatus;

          // Always update if status changed
          if (statusChanged) {
            console.log(`🔄 [POLL] Status changed: ${statusInfo.restoredStatus} → ${newStatus}`);

            let previewUrl = freshProject.previewUrl;
            if (!previewUrl && freshProject.category && freshProject.jobId) {
              const pythonBackendUrl = getPythonBaseUrl().replace(/\/+$/, '');
              previewUrl = `${pythonBackendUrl}/mesh-viewer?subpath=${freshProject.category}/${freshProject.jobId}`;
            }

            // Update highest status ref for restored sessions
            const statusOrder = ['created', 'file_pending', 'file_received', 'queued', 'in_progress', 'processing', 'review', 'ready', 'completed'];
            const newStatusIndex = statusOrder.indexOf(newStatus);
            if (newStatusIndex > highestStatusIndexRef.current) {
              highestStatusIndexRef.current = newStatusIndex;
            }

            setStatusInfo(prev => ({
              ...prev,
              preview_url: previewUrl || prev.preview_url,
              cloud_url: freshProject.cloudUrl || prev.cloud_url,
              restoredStatus: newStatus,
              jobProgress: newStatus === "review" || newStatus === "ready" ? 100 : prev.jobProgress,
            }));

            if (newStatus === "review") {
              addSystemLog(t("upload.resultReadyForReview"), "success");
              showToast(t("upload.processingDone"), "success");
            }
          }
        }
      } catch (err) {
        // Silently ignore polling errors
      }
    };

    // Poll immediately on mount, then every 3 seconds
    pollForStatus();
    const pollInterval = setInterval(pollForStatus, 3000);

    return () => clearInterval(pollInterval);
  }, [hasActiveSession, statusInfo.jobId, statusInfo.restoredStatus, ws.jobStatus]);

  // =============== ACTIONS ===============
  async function extractFramesToZip(file) {
    setIsExtracting(true);
    setExtractionProgress(0);

    const targetFrames = categories.find((c) => c.value === selectedCategory)?.frames || 100;
    addSystemLog(`Frame-Extraktion gestartet (${targetFrames} Frames)`, "info");

    const video = document.createElement("video");
    video.src = URL.createObjectURL(file);
    video.muted = true;
    await new Promise((resolve) => video.addEventListener("loadedmetadata", resolve));

    const canvas = document.createElement("canvas");
    const scale = Math.min(1, 1280 / video.videoWidth);
    canvas.width = video.videoWidth * scale;
    canvas.height = video.videoHeight * scale;
    const ctx = canvas.getContext("2d");

    const zip = new JSZip();
    const intervalSeconds = video.duration / targetFrames;

    const seekToTime = (time) => {
      return new Promise((resolve) => {
        const onSeeked = () => {
          video.removeEventListener("seeked", onSeeked);
          setTimeout(resolve, 50);
        };
        video.addEventListener("seeked", onSeeked);
        video.currentTime = time;
      });
    };

    for (let i = 0; i < targetFrames; i++) {
      if (abortRequestedRef.current) break;
      const currentTime = Math.min(i * intervalSeconds, video.duration - 0.001);
      await seekToTime(currentTime);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.85));
      zip.file(`frame_${String(i).padStart(4, "0")}.jpg`, blob);

      if (i === 0) {
        setThumbnailBlob(blob);
      }

      setExtractionProgress(Math.round(((i + 1) / targetFrames) * 100));
    }

    setIsExtracting(false);
    if (abortRequestedRef.current) return null;

    addSystemLog("Archiv wird erstellt...", "info");
    const zipBlob = await zip.generateAsync({ type: "blob" });
    addSystemLog(`Archiv fertig: ${(zipBlob.size / (1024 * 1024)).toFixed(2)} MB`, "success");
    return zipBlob;
  }

  async function startProcess() {
    if (!selectedFile) return;

    // Prevent concurrent calls (double-click protection)
    if (isStartingProcessRef.current) {
      console.log("⚠️ startProcess already running, ignoring duplicate call");
      return;
    }

    if (!currentUser?.username || currentUser.username === "ANONYMOUS") {
      addSystemLog("Bitte zuerst anmelden", "error");
      showToast("Bitte melden Sie sich an, um fortzufahren.", "error");
      return;
    }

    // VERIFY THE JWT IS STILL VALID before starting the scan. If the auth
    // cookie has expired or is missing, getJobId() later would silently fail
    // and the scan would never be registered in the user's project history.
    // Force a clean logout + redirect so the user re-authenticates instead of
    // burning a scan that ends up invisible.
    try {
      await apiService.checkAuth();
    } catch (authErr) {
      const msg = authErr?.message || '';
      const isAuthFailure = msg.includes('Token fehlt') || msg.includes('Not authenticated')
        || msg.includes('401') || msg.includes('Unauthorized') || msg.includes('Ungültiges Token');
      if (isAuthFailure) {
        addSystemLog("Sitzung abgelaufen – bitte erneut anmelden", "error");
        showToast("Ihre Sitzung ist abgelaufen. Sie werden zur Anmeldung weitergeleitet.", "error");
        try { await apiService.logoutUser(currentUser.username); } catch (_) { /* ignore */ }
        logout();
        // Small delay so the user actually sees the toast before navigation.
        setTimeout(() => navigate('/login', { replace: true }), 800);
        return;
      }
      // Non-auth error talking to the backend — let it surface but don't block.
      console.warn('checkAuth() returned an unexpected error before scan start:', authErr);
    }

    // Only block if THIS session is currently uploading (not during processing - backend supports queue)
    // Backend now supports multiple concurrent jobs per user - one active, rest queued
    const isCurrentlyUploading = isUplinking || uploadProgress > 0 || isExtracting;

    if (isCurrentlyUploading) {
      addSystemLog(t("upload.uploadAlreadyRunning"), "warning");
      showToast(t("upload.uploadAlreadyRunning"), "warning");
      return;
    }

    // Block only if no credits AND no free scan attempts remaining
    const isFreeUser = scanLimits && !scanLimits.unlimited;
    const hasCredits = credits.total !== null && credits.total > 0;
    const hasFreeAttempts = isFreeUser && scanLimits.daily && scanLimits.daily.used < scanLimits.daily.limit
      && scanLimits.weekly.used < scanLimits.weekly.limit
      && scanLimits.monthly.used < scanLimits.monthly.limit;

    if (!hasCredits && !hasFreeAttempts) {
      if (isFreeUser) {
        addSystemLog("Scan-Limit erreicht", "error");
        showToast("Sie haben Ihr Limit an kostenlosen Scan-Versuchen erreicht. Bitte upgraden Sie Ihr Abo.", "warning");
      } else {
        addSystemLog("Kein Guthaben vorhanden", "error");
        showToast(t("upload.noCreditsAvailable"), "warning");
      }
      return;
    }

    // Set lock to prevent concurrent calls
    isStartingProcessRef.current = true;
    abortRequestedRef.current = false;

    try {
      addSystemLog("Job wird initialisiert...", "info");

      // forceNew: true because user is intentionally starting a new scan
      const jobIdResponse = await apiService.getJobId(selectedCategory, selectedFile.name, 0, true);
      if (!jobIdResponse || !jobIdResponse.job_id) {
        throw new Error("Job-Initialisierung fehlgeschlagen");
      }
      const { job_id, queue_position, is_queued, creditDeducted, remainingCredits, trialScan, freeScansRemaining } = jobIdResponse;

      // Credit was deducted immediately - refresh display
      if (creditDeducted) {
        await fetchDownloadBalance();
        addSystemLog(`Guthaben abgezogen (${remainingCredits} verbleibend)`, "info");
      }

      // Trial scan: update remaining attempts and show info
      if (trialScan) {
        setIsCurrentTrialScan(true);
        if (freeScansRemaining) {
          setScanLimits(prev => ({ ...prev, ...freeScansRemaining }));
        }
        addSystemLog("Kostenloser Vorschau-Scan gestartet (kein Download)", "info");
        showToast(t("upload.trialScanInfo"), "info");
      } else {
        setIsCurrentTrialScan(false);
      }

      // Inform user if their scan is queued behind another
      if (is_queued && queue_position > 0) {
        addSystemLog(`Scan in Warteschlange (Position: ${queue_position})`, "info");
        showToast(`Ihr vorheriger Scan wird noch verarbeitet. Dieser Scan startet automatisch danach.`, "info");
      }

      // ✅ Update ref SYNCHRONOUSLY before async state update (prevents race condition)
      currentJobIdRef.current = job_id;
      lastSyncedStatusRef.current = null; // Reset sync tracker for new job
      highestStatusIndexRef.current = -1; // Reset highest status tracker for new job
      highestVisualStepRef.current = -1; // Reset visual-step tracker for new job

      setStatusInfo(prev => ({
        ...prev,
        jobId: job_id,
        pythonJobId: null,
        preview_url: null,
        cloud_url: null,
        restoredStatus: null,
        jobProgress: 0,
        queuePosition: queue_position || 0,
        isQueued: is_queued || false,
      }));
      setHasActiveSession(false);
      setIsPreviewAccepted(false);
      addSystemLog(`Job erstellt: ${job_id}`, "success");

      const connectPromise = !ws.isConnected ? ws.connect() : Promise.resolve();
      const zipPromise = extractFramesToZip(selectedFile);

      addSystemLog("Daten werden vorbereitet...", "info");

      const [_, zipBlob] = await Promise.all([connectPromise, zipPromise]);
      if (!zipBlob) {
        setIsUplinking(false);
        return;
      }

      setIsUplinking(true);

      const filename = `${job_id}_frames.zip`;
      ws.sendJson({
        action: "upload_init",
        data: {
          job_id: job_id,
          category: selectedCategory,
          filename: filename,
          size: zipBlob.size
        }
      });

      const chunkSize = 64 * 1024;
      for (let offset = 0; offset < zipBlob.size; offset += chunkSize) {
        if (abortRequestedRef.current) break;
        await ws.sendBuffer(await zipBlob.slice(offset, offset + chunkSize).arrayBuffer());
        setUploadProgress(Math.round((offset / zipBlob.size) * 100));
      }
      if (!abortRequestedRef.current) {
        setUploadProgress(100);
        setTimeout(() => {
          ws.sendJson({ action: "start_process" });
          addSystemLog("Verarbeitung gestartet", "success");
        }, 300);
      }
    } catch (err) {
      addSystemLog(err.message, "error");
      showToast("Fehler: " + err.message, "error");
      setIsExtracting(false);
      setIsUplinking(false);
    } finally {
      setIsUplinking(false);
      isStartingProcessRef.current = false; // Release lock
    }
  }

  function abortProcess() {
    ws.sendJson({ action: "abort_request" });
    addSystemLog("Abbruch angefordert", "warning");
  }

  function resetProcess() {
    abortRequestedRef.current = true;
    currentJobIdRef.current = null; // ✅ Clear job ID ref
    lastSyncedStatusRef.current = null;
    highestStatusIndexRef.current = -1; // ✅ Reset highest status tracker
    highestVisualStepRef.current = -1; // ✅ Reset visual-step tracker
    ws.disconnect(); // ✅ Close WebSocket connection
    setHasActiveSession(false); // ✅ Clear active session flag
    setUploadProgress(0);
    setExtractionProgress(0);
    setIsExtracting(false);
    setIsUplinking(false);
    setSelectedFile(null);
    setIsPreviewAccepted(false);
    setStatusInfo({
      jobId: null,
      category: "Standard",
      preview_url: null,
      cloud_url: null,
      jobProgress: 0,
      workerNum: 0,
      clientNum: 0,
      queueLength: 0,
      queuePosition: null,
    });
    addSystemLog(t("upload.reset"), "info");
    if (videoInputRef.current) videoInputRef.current.value = "";
  }

  const [thumbnailBlob, setThumbnailBlob] = useState(null);

  async function handleAcceptAndSave() {
    const nodeJobId = statusInfo.jobId || statusInfo.pythonJobId || ws.jobId;

    if (!nodeJobId) {
      showToast("Keine Job-ID gefunden.", "error");
      return;
    }

    if (isAccepting) return;
    setIsAccepting(true);

    try {
      addSystemLog(t("upload.filesTransferring"), "info");
      const result = await apiService.acceptProject(nodeJobId);

      // Store project title for download filenames
      if (result?.project?.title) {
        projectTitleRef.current = result.project.title;
      }

      // Consume credits after accepting (project is now in "ready" status)
      try {
        const consumeResult = await apiService.consumeDownload(nodeJobId);
        await fetchDownloadBalance(); // Refresh credit display
        if (!consumeResult.alreadyUnlocked) {
          addSystemLog("Download-Guthaben verbraucht", "info");
        }
        // Store download token for client-side export
        downloadTokenRef.current = consumeResult.downloadToken;
      } catch (consumeErr) {
        console.error("Credit consumption failed:", consumeErr);
        // Don't block acceptance if credit consumption fails
        addSystemLog("Hinweis: Guthaben-Abzug fehlgeschlagen", "warning");
      }

      setIsPreviewAccepted(true);
      addSystemLog("Ergebnis akzeptiert", "success");
      showToast(t("upload.resultAccepted"), "success");
    } catch (err) {
      console.error("Akzeptieren fehlgeschlagen:", err);
      const errorMsg = err.message || "Akzeptieren fehlgeschlagen.";

      if (errorMsg.includes("not in review")) {
        showToast("Projekt bereits akzeptiert oder nicht bereit.", "warning");
        setIsPreviewAccepted(true);
      } else if (errorMsg.includes("Not authorized")) {
        showToast(t("upload.noPermission"), "error");
      } else {
        showToast("Fehler: " + errorMsg, "error");
      }
    } finally {
      setIsAccepting(false);
    }

    // Only upload thumbnail if blob is valid (has size > 0)
    if (thumbnailBlob && thumbnailBlob.size > 0 && statusInfo.jobId) {
      apiService.uploadThumbnail(statusInfo.jobId, thumbnailBlob)
        .catch(err => console.error("Thumbnail-Upload fehlgeschlagen:", err));
    }
  }

  function handleRejectSession() {
    setShowFeedbackModal(true);
  }

  function handleFeedbackClose() {
    // User cancelled feedback - just close modal, stay on preview
    setShowFeedbackModal(false);
  }

  function handleFeedbackSubmitted() {
    // Feedback was actually submitted - reset and start fresh
    setShowFeedbackModal(false);
    resetProcess();
  }

  // Open download format modal (credits already consumed at accept time)
  async function handleExport() {
    const jobId = statusInfo.jobId || statusInfo.pythonJobId || ws.jobId;
    if (!jobId) return;

    // If no token yet (e.g. session restored), consume download to get one
    if (!downloadTokenRef.current) {
      setIsConsuming(true);
      try {
        const data = await apiService.consumeDownload(jobId);
        await fetchDownloadBalance();
        downloadTokenRef.current = data.downloadToken;
        addSystemLog(data.alreadyUnlocked ? t("upload.downloadReopened") : t("upload.creditConsumed"), "success");
      } catch (err) {
        console.error("Export-Fehler:", err);
        const errorMsg = err.message || "Download fehlgeschlagen.";
        addSystemLog(errorMsg, "error");
        if (errorMsg.includes("bestätigt") || errorMsg.includes("nicht verfügbar")) {
          showToast("Bitte zuerst Ergebnis akzeptieren.", "warning");
        } else if (errorMsg.includes("Keine verbleibenden Downloads") || errorMsg.includes("403")) {
          showToast("Kein Guthaben mehr. Bitte aufladen.", "warning");
        } else {
          showToast("Download fehlgeschlagen: " + errorMsg, "error");
        }
        setIsConsuming(false);
        return;
      } finally {
        setIsConsuming(false);
      }
    }

    setIsDownloadModalOpen(true);
  }

  // Handle format selection and trigger export via iframe postMessage
  function handleDownloadConfirm() {
    if (!iframeRef.current?.contentWindow) {
      showToast(t("upload.previewNotAvailable"), "error");
      return;
    }

    setIsDownloading(true);
    setIsDownloadModalOpen(false);

    // Use project title (like ProjectDetails page), fallback to category + jobId
    const projectName = projectTitleRef.current || 'model';

    iframeRef.current.contentWindow.postMessage({
      type: 'download-3d-model',
      format: downloadFormat,
      projectName,
      downloadToken: downloadTokenRef.current
    }, '*');

    // Timeout in case the iframe never responds
    setTimeout(() => {
      setIsDownloading(false);
    }, 60000);
  }

  return (
    <div className="min-h-[calc(100vh-5rem)] lg:h-[calc(100vh-5rem)] bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 font-sans select-none flex flex-col lg:overflow-hidden">
      {/* Page Header - Full Width */}
      <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 shrink-0">
        <div className="px-3 sm:px-6 lg:px-8 py-2 sm:py-5">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-14 sm:h-14 bg-[#0f7699]/10 dark:bg-[#4fc3f7]/15 rounded-xl sm:rounded-2xl flex items-center justify-center shrink-0">
              <ScanLine className="text-[#0f7699] dark:text-[#4fc3f7]" size={22} />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-lg sm:text-2xl md:text-3xl font-black text-[#0f7699] dark:text-[#4fc3f7] tracking-tight">
                {t("upload.pageTitle")}
              </h1>
              <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 font-medium mt-0.5 hidden sm:block">
                {t("upload.pageSubtitle")}
              </p>
            </div>

            {/* User Info in Header */}
            <div className="hidden md:flex items-center gap-4">
              {/* User Details */}
              <div className="flex items-center gap-3 px-4 py-2.5 bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
                <div className="w-9 h-9 bg-[#0f7699]/10 dark:bg-[#4fc3f7]/15 rounded-lg flex items-center justify-center shrink-0">
                  <UserIcon className="text-[#0f7699] dark:text-[#4fc3f7]" size={18} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-bold text-slate-700 dark:text-slate-200 truncate">{currentUser.username?.toUpperCase() || t("upload.anonymous")}</p>
                  <p className="text-[10px] text-slate-500 dark:text-slate-400">{isAdmin ? "Admin" : t("upload.user")}</p>
                </div>
              </div>

              {/* Credit Display with Hover Details */}
              <div className="relative group/credits">
                <div className="flex items-center gap-3 px-4 py-2.5 bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 min-w-[220px] cursor-pointer hover:border-[#0f7699]/50 dark:hover:border-[#4fc3f7]/50 transition-colors">
                  <div className="w-9 h-9 bg-[#0f7699]/10 dark:bg-[#4fc3f7]/15 rounded-lg flex items-center justify-center shrink-0">
                    <Download className="text-[#0f7699] dark:text-[#4fc3f7]" size={18} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wider font-semibold">{t("upload.credits")}</span>
                      <span className="text-sm font-bold text-slate-800 dark:text-white">{credits.total !== null ? credits.total : "..."}<span className="text-[10px] text-slate-400 dark:text-slate-500 ml-0.5 font-normal">{t("upload.available")}</span></span>
                    </div>
                    {/* Stacked Credit Bar - Order: Paket → Bonus → Abo remaining → Abo used (left to right) */}
                    {(() => {
                      // Calculate max for bar including used credits
                      const totalWithUsed = credits.package + credits.bonus + credits.subscriptionTotal;
                      const maxForBar = Math.max(50, totalWithUsed || 1);
                      return (
                        <div className="h-2.5 bg-slate-300 dark:bg-slate-600 rounded-full overflow-hidden flex">
                          {credits.package > 0 && (
                            <div
                              className="bg-emerald-500 h-full transition-all duration-500"
                              style={{ width: `${(credits.package / maxForBar) * 100}%` }}
                              title={`Paket: ${credits.package}`}
                            />
                          )}
                          {credits.bonus > 0 && (
                            <div
                              className="bg-violet-500 h-full transition-all duration-500"
                              style={{ width: `${(credits.bonus / maxForBar) * 100}%` }}
                              title={`Bonus: ${credits.bonus}`}
                            />
                          )}
                          {credits.subscription > 0 && (
                            <div
                              className="bg-[#0f7699] dark:bg-[#4fc3f7] h-full transition-all duration-500"
                              style={{ width: `${(credits.subscription / maxForBar) * 100}%` }}
                              title={`Abo verfügbar: ${credits.subscription}`}
                            />
                          )}
                          {credits.subscriptionUsed > 0 && (
                            <div
                              className="bg-slate-400/60 dark:bg-slate-500/60 h-full transition-all duration-500"
                              style={{ width: `${(credits.subscriptionUsed / maxForBar) * 100}%` }}
                              title={`Abo verbraucht: ${credits.subscriptionUsed}`}
                            />
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>

                {/* Hover Dropdown with Details */}
                <div className="absolute top-full right-0 mt-2 w-80 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-xl opacity-0 invisible group-hover/credits:opacity-100 group-hover/credits:visible transition-all duration-200 z-50 p-4">
                  {/* Breakdown Items - Visual order matches bar: Paket, Bonus, Abo */}
                  <div className="space-y-2.5">
                    {/* Paket-Guthaben */}
                    <div className="p-2.5 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800/30">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                          <span className="text-xs font-medium text-slate-700 dark:text-slate-300">{t("upload.packageCredits")}</span>
                          <span className="text-[9px] px-1.5 py-0.5 bg-emerald-100 dark:bg-emerald-800/50 text-emerald-600 dark:text-emerald-400 rounded font-bold">3.</span>
                        </div>
                        <div className="text-right">
                          <span className="text-sm font-bold text-emerald-500 dark:text-emerald-400">{credits.package}</span>
                          <span className="text-[10px] text-slate-400 dark:text-slate-500 ml-1">{t("upload.available")}</span>
                        </div>
                      </div>
                    </div>

                    {/* Bonus-Guthaben */}
                    <div className="p-2.5 rounded-lg bg-violet-50 dark:bg-violet-900/20 border border-violet-100 dark:border-violet-800/30">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-2.5 h-2.5 rounded-full bg-violet-500" />
                          <span className="text-xs font-medium text-slate-700 dark:text-slate-300">{t("upload.bonusCredits")}</span>
                          <span className="text-[9px] px-1.5 py-0.5 bg-violet-100 dark:bg-violet-800/50 text-violet-600 dark:text-violet-400 rounded font-bold">2.</span>
                        </div>
                        <div className="text-right">
                          <span className="text-sm font-bold text-violet-500 dark:text-violet-400">{credits.bonus}</span>
                          <span className="text-[10px] text-slate-400 dark:text-slate-500 ml-1">{t("upload.available")}</span>
                        </div>
                      </div>
                    </div>

                    {/* Abo-Guthaben */}
                    <div className="p-2.5 rounded-lg bg-[#0f7699]/10 dark:bg-[#4fc3f7]/10 border border-[#0f7699]/20 dark:border-[#4fc3f7]/20">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <div className="w-2.5 h-2.5 rounded-full bg-[#0f7699] dark:bg-[#4fc3f7]" />
                          <span className="text-xs font-medium text-slate-700 dark:text-slate-300">{t("upload.subCredits")}</span>
                          <span className="text-[9px] px-1.5 py-0.5 bg-[#0f7699]/20 dark:bg-[#4fc3f7]/30 text-[#0f7699] dark:text-[#4fc3f7] rounded font-bold">1.</span>
                        </div>
                        <div className="text-right">
                          <span className="text-sm font-bold text-[#0f7699] dark:text-[#4fc3f7]">{credits.subscription}</span>
                          <span className="text-[10px] text-slate-400 dark:text-slate-500">/{credits.subscriptionTotal || "∞"}</span>
                        </div>
                      </div>
                      {credits.subscriptionUsed > 0 && (
                        <div className="flex items-center justify-between pl-4 mt-1">
                          <div className="flex items-center gap-1.5">
                            <div className="w-2 h-2 rounded-full bg-slate-400/60 dark:bg-slate-500/60" />
                            <span className="text-[10px] text-slate-500 dark:text-slate-400">{t("upload.used")}</span>
                          </div>
                          <span className="text-[10px] font-medium text-slate-500 dark:text-slate-400">{credits.subscriptionUsed}</span>
                        </div>
                      )}
                      {credits.nextBillingDate && (
                        <p className="text-[9px] text-slate-500 dark:text-slate-400 pl-4 mt-1">{t("upload.renewsOn")} {credits.nextBillingDate}</p>
                      )}
                    </div>
                  </div>

                  {/* Footer Info */}
                  <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-700">
                    <p className="text-[10px] text-slate-500 dark:text-slate-400">
                      <span className="font-semibold text-slate-600 dark:text-slate-300">{t("upload.usageOrder")}:</span> {t("upload.usageOrderDesc")}
                    </p>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </header>

      {/* Main Layout Container */}
      <div className="flex flex-col lg:flex-row flex-1 lg:min-h-0 lg:overflow-hidden">
      {/* WORKFLOW STEPPER - Horizontal on mobile, Vertical on large screens */}
      <nav className="w-full lg:w-64 shrink-0 border-b lg:border-b-0 lg:border-r border-slate-200 dark:border-slate-800 px-3 py-2 lg:p-6 lg:pt-8 bg-white dark:bg-slate-900/50 z-30 flex flex-col lg:overflow-y-auto">
        <h3 className="hidden lg:block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-8">{t("upload.workflowSteps")}</h3>

        {/* Horizontal stepper for mobile */}
        <div className="flex lg:hidden items-center justify-center gap-0 pb-1">
          {steps.map((step, idx) => {
            const isFailed = ws.jobStatus === "failed" && idx === 3;
            const isCompleted = visualStep > idx;
            const isActive = visualStep === idx;

            return (
              <div key={step.id} className="flex items-center">
                <div className={`shrink-0 w-7 h-7 rounded-full border-2 flex items-center justify-center ${
                  isFailed
                    ? "bg-red-100 dark:bg-red-900/30 border-red-500 text-red-600"
                    : isCompleted
                      ? "bg-[#0f7699]/10 dark:bg-[#4fc3f7]/15 border-[#0f7699] dark:border-[#4fc3f7] text-[#0f7699] dark:text-[#4fc3f7]"
                      : isActive
                        ? "bg-[#0f7699]/5 dark:bg-[#4fc3f7]/10 border-[#0f7699] dark:border-[#4fc3f7] text-[#0f7699] dark:text-[#4fc3f7] ring-2 ring-[#0f7699]/20 dark:ring-[#4fc3f7]/20"
                        : "bg-slate-50 dark:bg-slate-900 border-slate-300 dark:border-slate-700 text-slate-400"
                }`}>
                  {isFailed ? <XCircle size={12} /> : isCompleted ? <Check size={12} /> : <span className="text-[10px] font-semibold">{idx + 1}</span>}
                </div>
                {idx < steps.length - 1 && (
                  <div className={`w-5 h-0.5 ${isCompleted ? 'bg-[#0f7699]' : 'bg-slate-200 dark:bg-slate-700'}`} />
                )}
              </div>
            );
          })}
        </div>

        {/* Vertical stepper for desktop */}
        <div className="hidden lg:block relative space-y-8">
          {/* Vertical line */}
          <div className="absolute left-[19px] top-5 bottom-5 w-0.5 bg-slate-200 dark:bg-slate-800" />

          {steps.map((step, idx) => {
            const isFailed = ws.jobStatus === "failed" && idx === 3;
            const isCompleted = visualStep > idx;
            const isActive = visualStep === idx;
            const statusText = getStepStatus(idx);

            return (
              <WorkflowStep
                key={step.id}
                step={{ ...step, translatedLabel: t(step.labelKey) }}
                index={idx}
                isCompleted={isCompleted}
                isActive={isActive}
                isFailed={isFailed}
                statusText={statusText}
              />
            );
          })}
        </div>

        {/* Progress info for mobile */}
        <div className="lg:hidden mt-1 text-center">
          <span className="text-xs font-medium text-slate-700 dark:text-slate-300">
            {t(steps[Math.max(0, Math.min(visualStep, steps.length - 1))].labelKey)}
          </span>
          <span className="text-[10px] text-slate-500 dark:text-slate-400 ml-2">
            {getStepStatus(visualStep)}
          </span>
        </div>
      </nav>

      {/* MAIN CONTENT AREA */}
      <main className="flex-1 flex flex-col bg-slate-50 dark:bg-slate-950 relative lg:min-h-0 lg:overflow-hidden">
        {/* Main content section */}
        <section className="flex-1 flex flex-col items-center justify-center p-3 md:p-8 lg:overflow-y-auto">
          {/* No file selected and no active session */}
          {!selectedFile && !hasActiveSession ? (
            <div className="max-w-lg w-full p-5 md:p-12 border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-2xl bg-white dark:bg-slate-900 text-center hover:border-[#0f7699] dark:hover:border-[#4fc3f7] transition-colors cursor-pointer group"
                 onClick={() => videoInputRef.current?.click()}>
              <div className="w-12 h-12 md:w-16 md:h-16 bg-slate-100 dark:bg-slate-800 rounded-xl md:rounded-2xl flex items-center justify-center mx-auto mb-3 md:mb-6 group-hover:bg-[#0f7699]/5 dark:group-hover:bg-[#4fc3f7]/15 transition-colors">
                <FileVideo className="text-slate-400 dark:text-slate-500 group-hover:text-[#0f7699] dark:group-hover:text-[#4fc3f7]" size={24} />
              </div>
              <h3 className="text-base md:text-lg font-bold mb-1 md:mb-2 text-[#0f7699] dark:text-[#4fc3f7]">{t("upload.selectVideo")}</h3>
              <p className="text-xs md:text-sm text-slate-500 dark:text-slate-400 mb-3 md:mb-6">{t("upload.uploadVideoDesc")}</p>
              <button className="px-6 py-2.5 md:px-8 md:py-3 bg-[#0f7699] dark:bg-[#4fc3f7] text-white dark:text-slate-900 rounded-xl font-bold text-sm hover:bg-[#0f7699]/90 dark:hover:bg-[#4fc3f7]/90 transition-colors shadow-sm">
                {t("upload.selectFile")}
              </button>
              <input type="file" hidden ref={videoInputRef} accept="video/*" onChange={e => setSelectedFile(e.target.files[0])} />
            </div>
          ) : selectedFile && !isExtracting && !isUplinking && uploadProgress === 0 && !statusInfo.jobId && !hasActiveSession ? (
            /* File selected, ready to start - show video preview with category selection */
            <div className="w-full max-w-4xl animate-in fade-in duration-500">
              {/* Video Preview */}
              <div className="relative aspect-video bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden mb-6 shadow-lg">
                <video
                  src={URL.createObjectURL(selectedFile)}
                  className="absolute inset-0 w-full h-full object-cover"
                  muted
                  loop
                  autoPlay
                  playsInline
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4 text-white">
                  <p className="text-sm font-medium opacity-80">{selectedFile.name}</p>
                  <p className="text-xs opacity-60">{(selectedFile.size / (1024 * 1024)).toFixed(1)} MB</p>
                </div>
              </div>
              {/* Category selection and start button */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800">
                  <span className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-2 block">{t("upload.category")}</span>
                  {/* Desktop: native select */}
                  <select
                    value={selectedCategory}
                    onChange={e => setSelectedCategory(e.target.value)}
                    className="hidden md:block w-full bg-transparent border-none text-sm font-medium text-slate-800 dark:text-slate-200 focus:ring-0 p-0 cursor-pointer"
                  >
                    {categories.map(c => <option key={c.value} value={c.value} className="bg-white dark:bg-slate-900">{t(c.labelKey)}</option>)}
                  </select>
                  {/* Mobile: touch button → opens bottom sheet */}
                  <button
                    onClick={() => setShowCategorySheet(true)}
                    className="md:hidden w-full flex items-center justify-between text-sm font-medium text-slate-800 dark:text-slate-200"
                  >
                    <span>{t(CATEGORY_LABELS[selectedCategory] || "upload.categoryDefault")}</span>
                    <ChevronDown size={16} className="text-slate-400" />
                  </button>
                </div>
                <div className="p-4 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 flex items-center justify-between">
                  <div>
                    <span className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1 block">Status</span>
                    <span className="text-sm font-medium text-[#0f7699] dark:text-[#4fc3f7]">{t("upload.ready")}</span>
                  </div>
                  <button
                    onClick={startProcess}
                    className="px-6 py-2.5 bg-[#0f7699] dark:bg-[#4fc3f7] text-white rounded-lg font-medium text-sm hover:bg-[#0f7699]/90 dark:hover:bg-[#4fc3f7]/90 transition-colors flex items-center space-x-2"
                  >
                    <Play size={16} />
                    <span>{t("upload.start")}</span>
                  </button>
                </div>
              </div>
            </div>
          ) : (selectedFile || hasActiveSession) && visualStep < 4 && !["review", "ready", "completed"].includes(statusInfo.restoredStatus) ? (
            /* Processing state - 50/50 side by side layout (only when upload started, not during extraction) */
            <div className={`flex flex-col lg:flex-row w-full h-full p-4 md:p-6 gap-4 animate-in fade-in duration-500 ${(isUplinking || uploadProgress > 0 || statusInfo.jobId) ? '' : 'max-w-3xl mx-auto'}`}>
              {/* Progress display - Left Side (50% when uploading, full width during extraction) */}
              <div className={`${(isUplinking || uploadProgress > 0 || statusInfo.jobId) ? 'lg:w-1/2' : 'w-full'} min-h-[50vh] lg:min-h-0 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 flex items-center justify-center overflow-hidden relative shadow-lg`}>
                {/* Progress overlay */}
                <div className="text-center z-10 p-6">
                    <div className="relative mb-4 h-16 w-16 mx-auto">
                      <div className="absolute inset-0 animate-ping bg-[#0f7699]/10 rounded-full" />
                      <div className="relative w-full h-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-full flex items-center justify-center">
                        {isExtracting ? (
                          <ScanLine className="text-[#0f7699] dark:text-[#4fc3f7] animate-pulse" size={24} />
                        ) : (uploadProgress > 0 && uploadProgress < 100) || isUplinking ? (
                          <Cloud className="text-[#0f7699] dark:text-[#4fc3f7] animate-pulse" size={24} />
                        ) : effectiveStatus === "queued" && uploadProgress >= 100 ? (
                          <Loader2 className="text-[#0f7699] dark:text-[#4fc3f7] animate-spin" size={24} />
                        ) : statusInfo.jobProgress > 0 && statusInfo.jobProgress < 100 ? (
                          // Dynamic icon based on processing stage
                          ws.processingStage === "vorbereiten" ? (
                            <Send className="text-[#0f7699] dark:text-[#4fc3f7] animate-pulse" size={24} />
                          ) : ws.processingStage === "photogrammetrie" || !ws.processingStage ? (
                            // Dynamic micro-stage icons for photogrammetry
                            (() => {
                              const microStage = getPhotogrammetryMicroStage(statusInfo.jobProgress);
                              const IconMap = { Search, Grid3X3, Calculator, Link2, Box, Sparkles, Gauge, CheckCircle2, Loader2 };
                              const IconComponent = IconMap[microStage.icon] || Loader2;
                              return <IconComponent className="text-[#0f7699] dark:text-[#4fc3f7] animate-pulse" size={24} />;
                            })()
                          ) : ws.processingStage === "abschliessen" ? (
                            <Package className="text-[#0f7699] dark:text-[#4fc3f7] animate-pulse" size={24} />
                          ) : ws.processingStage === "skalieren" ? (
                            <Maximize2 className="text-[#0f7699] dark:text-[#4fc3f7] animate-pulse" size={24} />
                          ) : ws.processingStage === "cloud-upload" ? (
                            <Cloud className="text-[#0f7699] dark:text-[#4fc3f7] animate-pulse" size={24} />
                          ) : (
                            <Loader2 className="text-[#0f7699] dark:text-[#4fc3f7] animate-spin" size={24} />
                          )
                        ) : statusInfo.jobProgress >= 100 ? (
                          <CheckCircle className="text-emerald-500" size={24} />
                        ) : (
                          <Database className="text-[#0f7699] dark:text-[#4fc3f7]" size={24} />
                        )}
                      </div>
                    </div>
                    <h3 className="text-sm font-semibold mb-2 text-[#0f7699] dark:text-[#4fc3f7]">
                      {isExtracting ? t("upload.stepAnalyzeVideo")
                        : (uploadProgress > 0 && uploadProgress < 100) || isUplinking ? t("upload.uploading")
                        : effectiveStatus === "queued" && uploadProgress >= 100 ? t("upload.inQueue")
                        : statusInfo.jobProgress > 0 && statusInfo.jobProgress < 100 ? t(getProcessingStageLabel(ws.processingStage, statusInfo.jobProgress))
                        : statusInfo.jobProgress >= 100 ? t("upload.stepCheckResult")
                        : t("upload.processingRunning")}
                    </h3>

                    {/* Progress bars */}
                    <div className="w-full max-w-[14rem] mx-auto space-y-2 mt-3">
                      {isExtracting && (
                        <div>
                          <div className="flex justify-between text-xs text-slate-500 dark:text-slate-400 mb-1">
                            <span>{t("upload.stepAnalyzeVideo")}</span>
                            <span>{extractionProgress}%</span>
                          </div>
                          <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                            <div className="h-full bg-[#0f7699] transition-all duration-300 rounded-full" style={{ width: `${extractionProgress}%` }} />
                          </div>
                        </div>
                      )}
                      {/* Only show upload bar when uploading (not at 100%) and 3D model progress hasn't started */}
                      {((uploadProgress > 0 && uploadProgress < 100) || isUplinking) && statusInfo.jobProgress === 0 && (
                        <div>
                          <div className="flex justify-between text-xs text-slate-500 dark:text-slate-400 mb-1">
                            <span>Upload</span>
                            <span>{uploadProgress}%</span>
                          </div>
                          <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                            <div className="h-full bg-[#0f7699] transition-all duration-300 rounded-full" style={{ width: `${uploadProgress}%` }} />
                          </div>
                        </div>
                      )}
                      {/* Queue indicator when scan is waiting */}
                      {statusInfo.isQueued && statusInfo.jobProgress === 0 && uploadProgress >= 100 && (
                        <div>
                          <div className="flex justify-between text-xs text-slate-500 dark:text-slate-400 mb-1">
                            <span>3D-Modell</span>
                            <span className="text-orange-500">{t("upload.queue")}</span>
                          </div>
                          <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                            <div className="h-full bg-orange-400 animate-pulse rounded-full" style={{ width: '100%' }} />
                          </div>
                          <p className="text-[10px] text-orange-500 dark:text-orange-400 mt-1 text-center font-medium">
                            {t("upload.waitingForPreviousScan")}
                          </p>
                        </div>
                      )}
                      {statusInfo.jobProgress > 0 && (
                        <div>
                          <div className="flex justify-between text-xs text-slate-500 dark:text-slate-400 mb-1">
                            <span>3D-Modell</span>
                            <span>{Number(statusInfo.jobProgress).toFixed(2)}%</span>
                          </div>
                          <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                            <div className="h-full bg-[#0f7699] transition-all duration-300 rounded-full" style={{ width: `${statusInfo.jobProgress}%` }} />
                          </div>
                          {/* Sync status when accepted */}
                          {statusInfo.jobProgress >= 100 && isPreviewAccepted && (
                            <p className="text-[10px] text-emerald-500 dark:text-emerald-400 mt-1 text-center font-medium animate-pulse">
                              {t(syncStages[syncStageIndex])}...
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
              </div>

              {/* Right Side Panel (50%) - Project Notes - only when Nextcloud connected */}
              {(isUplinking || uploadProgress > 0 || statusInfo.jobId) && nextcloudStatus?.connected && (
              <div className="w-full lg:w-1/2 flex flex-col gap-4">
                  <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden flex-1 flex flex-col">
                    <div className="p-4 border-b border-slate-200 dark:border-slate-800 shrink-0">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                          <StickyNote className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                        </div>
                        <div className="text-left">
                          <h3 className="text-sm font-medium text-[#0f7699] dark:text-[#4fc3f7]">{t("upload.projectNotes")}</h3>
                          <p className="text-xs text-slate-500 dark:text-slate-400">{t("upload.cloudStorageDetails")}</p>
                        </div>
                      </div>
                    </div>
                    <div className="p-4 space-y-3 overflow-y-auto flex-1">
                      <input
                        type="text"
                        value={projectNotes.bodyRegion}
                        onChange={(e) => updateNote('bodyRegion', e.target.value)}
                        placeholder={t("upload.bodyRegion")}
                        className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-800 dark:text-slate-200 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                      />
                      <input
                        type="text"
                        value={projectNotes.indication}
                        onChange={(e) => updateNote('indication', e.target.value)}
                        placeholder={t("upload.indication")}
                        className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-800 dark:text-slate-200 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                      />
                      <input
                        type="text"
                        value={projectNotes.caseNumber}
                        onChange={(e) => updateNote('caseNumber', e.target.value)}
                        placeholder={t("upload.caseNumber")}
                        className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-800 dark:text-slate-200 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                      />
                      <textarea
                        value={projectNotes.diagnosis}
                        onChange={(e) => updateNote('diagnosis', e.target.value)}
                        placeholder={t("upload.diagnosisNotes")}
                        rows={4}
                        className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-800 dark:text-slate-200 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20 resize-none"
                      />
                      <button
                        onClick={saveProjectNotes}
                        disabled={savingNotes}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg font-medium text-sm hover:bg-purple-500 transition-colors disabled:opacity-50"
                      >
                        {savingNotes ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        {savingNotes ? t("upload.saving") : t("upload.save")}
                      </button>
                    </div>
                  </div>
              </div>
              )}
            </div>
          ) : (
            /* Review/Preview state - Side by side 50/50 layout */
            <div className="flex flex-col lg:flex-row w-full h-full p-4 md:p-6 gap-4 animate-in fade-in duration-500">
              {/* 3D Preview Container - Left Side (50%) */}
              <div className="lg:w-1/2 aspect-[4/3] lg:aspect-auto lg:min-h-0 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden relative shadow-lg">
                {statusInfo.preview_url ? (
                  <iframe ref={iframeRef} src={iframeSrc} className="absolute inset-0 w-full h-full border-none" title="3D-Vorschau" />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center p-12 text-center">
                    <div className="w-24 h-24 bg-slate-50 dark:bg-slate-800 rounded-2xl flex items-center justify-center mb-8 border border-slate-200 dark:border-slate-700">
                      <Loader2 size={40} className="text-[#0f7699] dark:text-[#4fc3f7] animate-spin" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2 text-[#0f7699] dark:text-[#4fc3f7]">{t("upload.modelBeingCreated")}</h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400">{t("upload.pleaseWaitModel")}</p>
                  </div>
                )}
              </div>

              {/* Mobile 3D viewer controls - shown below iframe on small screens */}
              {statusInfo.preview_url && (
                <div className="lg:hidden flex items-center gap-2">
                  <button
                    onClick={() => iframeRef.current?.contentWindow?.postMessage({ type: 'viewer-command', command: 'home' }, '*')}
                    className="flex-1 h-11 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl flex items-center justify-center gap-2 active:scale-[0.97] transition-all"
                  >
                    <Home size={16} className="text-[#0f7699] dark:text-[#4fc3f7]" />
                    <span className="text-xs font-bold text-slate-700 dark:text-slate-300">Home</span>
                  </button>
                  <button
                    onClick={() => iframeRef.current?.contentWindow?.postMessage({ type: 'viewer-command', command: 'toggleMesh' }, '*')}
                    className="flex-1 h-11 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl flex items-center justify-center gap-2 active:scale-[0.97] transition-all"
                  >
                    <Layers size={16} className="text-[#0f7699] dark:text-[#4fc3f7]" />
                    <span className="text-xs font-bold text-slate-700 dark:text-slate-300">{iframeMeshLabel}</span>
                  </button>
                </div>
              )}

              {/* Right Panel - Controls & Notes (50%) */}
              <div className="w-full lg:w-1/2 flex flex-col gap-4 overflow-y-auto">
                {/* Accept/Reject Section */}
                {!isPreviewAccepted && statusInfo.preview_url && (
                  <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-5 shadow-sm">
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="p-2 bg-[#0f7699]/10 dark:bg-[#4fc3f7]/15 rounded-lg">
                        <ShieldCheck className="text-[#0f7699] dark:text-[#4fc3f7]" size={20} />
                      </div>
                      <h3 className="text-sm font-semibold text-[#0f7699] dark:text-[#4fc3f7]">{t("upload.stepCheckResult")}</h3>
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">{t("upload.reviewDesc")}</p>
                    <div className="flex space-x-2">
                      <button
                        onClick={handleRejectSession}
                        disabled={isAccepting}
                        className="flex-1 py-2 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 rounded-lg font-medium text-sm hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors disabled:opacity-30"
                      >
                        {t("upload.reject")}
                      </button>
                      <button
                        onClick={handleAcceptAndSave}
                        disabled={isAccepting}
                        className="flex-1 py-2 bg-[#0f7699] dark:bg-[#4fc3f7] text-white rounded-lg font-medium text-sm hover:bg-[#0f7699]/90 dark:hover:bg-[#4fc3f7]/90 transition-colors flex items-center justify-center space-x-1.5 disabled:opacity-50"
                      >
                        {isAccepting ? (
                          <>
                            <Loader2 size={14} className="animate-spin" />
                            <span className="animate-pulse text-xs">{t(syncStages[syncStageIndex])}...</span>
                          </>
                        ) : (
                          <>
                            <CheckCircle size={14} />
                            <span>{t("upload.accept")}</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}

                {/* Accepted Status + Download Button */}
                {isPreviewAccepted && (
                  <div className="bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800 p-5">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="p-2 bg-green-100 dark:bg-green-800/30 rounded-lg">
                        <CheckCircle className="text-green-600 dark:text-green-400" size={20} />
                      </div>
                      <h3 className="text-sm font-semibold text-green-700 dark:text-green-400">{t("upload.accepted")}</h3>
                    </div>

                    {isCurrentTrialScan && credits.total <= 0 ? (
                      <>
                        <p className="text-xs text-amber-600 dark:text-amber-400/80 mb-3">{t("upload.trialScanDesc")}</p>
                        <Link
                          to="/pricing"
                          className="w-full py-2.5 bg-amber-500 text-white rounded-lg font-medium text-sm hover:bg-amber-600 transition-colors flex items-center justify-center space-x-2"
                        >
                          <Sparkles size={16} />
                          <span>{t("upload.buySubOrPackage")}</span>
                        </Link>
                      </>
                    ) : (
                      <>
                        <p className="text-xs text-green-600 dark:text-green-400/80 mb-4">{t("upload.acceptedDesc")}</p>
                        <button
                          onClick={handleExport}
                          disabled={isConsuming || isDownloading}
                          className="w-full py-2.5 bg-[#0f7699] dark:bg-[#4fc3f7] text-white rounded-lg font-medium text-sm hover:bg-[#0f7699]/90 dark:hover:bg-[#4fc3f7]/90 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50"
                        >
                          {isDownloading ? (
                            <>
                              <Loader2 size={16} className="animate-spin" />
                              <span>{t("upload.exporting")}</span>
                            </>
                          ) : (
                            <>
                              <Download size={16} />
                              <span>{t("upload.download3DModel")}</span>
                            </>
                          )}
                        </button>
                      </>
                    )}
                  </div>
                )}

                {/* Temp Storage Info - when project is not synced to Nextcloud */}
                {isPreviewAccepted && !nextcloudStatus?.connected && (
                  <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800 p-4">
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="p-1.5 bg-amber-100 dark:bg-amber-800/30 rounded-lg">
                        <AlertCircle className="text-amber-600 dark:text-amber-400" size={16} />
                      </div>
                      <h3 className="text-xs font-semibold text-amber-700 dark:text-amber-400">{t("upload.tempStorageTitle")}</h3>
                    </div>
                    <p className="text-xs text-amber-600 dark:text-amber-400/80">
                      {t("upload.tempStorageDesc")}
                    </p>
                  </div>
                )}

                {/* Project Notes Section - only when Nextcloud connected */}
                {statusInfo.jobId && nextcloudStatus?.connected && (
                  <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden flex-1 flex flex-col min-h-0">
                    <div className="p-4 flex items-center gap-3 shrink-0">
                      <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30">
                        <StickyNote className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                      </div>
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{t("upload.projectNotes")}</span>
                    </div>
                    <div className="p-4 pt-0 border-t border-slate-200 dark:border-slate-800 space-y-3 overflow-y-auto flex-1">
                      <input type="text" value={projectNotes.bodyRegion} onChange={(e) => updateNote('bodyRegion', e.target.value)} placeholder={t("upload.bodyRegion")} className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-800 dark:text-slate-200 placeholder-slate-400 dark:placeholder-slate-500" />
                      <input type="text" value={projectNotes.indication} onChange={(e) => updateNote('indication', e.target.value)} placeholder={t("upload.indication")} className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-800 dark:text-slate-200 placeholder-slate-400 dark:placeholder-slate-500" />
                      <input type="text" value={projectNotes.caseNumber} onChange={(e) => updateNote('caseNumber', e.target.value)} placeholder={t("upload.caseNumber")} className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-800 dark:text-slate-200 placeholder-slate-400 dark:placeholder-slate-500" />
                      <input type="text" value={projectNotes.department} onChange={(e) => updateNote('department', e.target.value)} placeholder={t("upload.department")} className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-800 dark:text-slate-200 placeholder-slate-400 dark:placeholder-slate-500" />
                      <textarea value={projectNotes.diagnosis} onChange={(e) => updateNote('diagnosis', e.target.value)} placeholder={t("upload.diagnosisNotes")} rows={3} className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-800 dark:text-slate-200 placeholder-slate-400 dark:placeholder-slate-500 resize-none" />
                      <button onClick={saveProjectNotes} disabled={savingNotes} className="w-full px-4 py-2.5 bg-purple-600 text-white rounded-lg font-medium text-sm hover:bg-purple-500 disabled:opacity-50 flex items-center justify-center gap-2">
                        {savingNotes ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        {savingNotes ? t("upload.saving") : t("upload.saveNotes")}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </section>

        {/* Footer — Admin info bar, items wrap on mobile */}
        {isAdmin && (
        <footer className="shrink-0 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex flex-wrap items-center justify-between p-2 md:px-6 md:py-3 gap-2 z-50">
          <div className="flex items-center gap-3 flex-wrap flex-1">
            <div className="flex items-center gap-1.5 text-[10px] md:text-xs">
              <span className="text-slate-400 font-medium">Job:</span>
              <span className="font-bold text-slate-700 dark:text-slate-200" title={ws.jobId || statusInfo.jobId || "---"}>{ws.jobId || statusInfo.jobId || "---"}</span>
            </div>
            <div className="flex items-center gap-1.5 text-[10px] md:text-xs">
              <span className="text-slate-400 font-medium">{t("upload.category")}:</span>
              <span className="font-bold text-[#0f7699] dark:text-[#4fc3f7]">{t(CATEGORY_LABELS[statusInfo.category] || CATEGORY_LABELS[selectedCategory] || "upload.categoryDefault")}</span>
            </div>
            <div className="flex items-center gap-1.5 text-[10px] md:text-xs">
              <span className="text-slate-400 font-medium">Queue:</span>
              <span className="font-bold text-slate-700 dark:text-slate-200">{statusInfo.queuePosition ?? "---"}</span>
            </div>
            <div className="flex items-center gap-1.5 text-[10px] md:text-xs">
              <span className="text-slate-400 font-medium">Worker:</span>
              <span className="font-bold text-[#0f7699] dark:text-[#4fc3f7]">{(visualStep >= 3 && !statusInfo.workerNum) ? 1 : (statusInfo.workerNum ?? 0)}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {systemLogs.length > 0 && (
              <div className="hidden md:flex items-center gap-2 text-[10px] text-slate-500 dark:text-slate-400">
                <div className={`w-1.5 h-1.5 rounded-full ${
                  systemLogs[0]?.type === "error" ? "bg-red-500" :
                  systemLogs[0]?.type === "success" ? "bg-[#0f7699]" :
                  systemLogs[0]?.type === "warning" ? "bg-amber-500" : "bg-blue-500"
                }`} />
                <span className="truncate max-w-[200px]">{systemLogs[0]?.message}</span>
              </div>
            )}
            <button
              onClick={() => setShowLogsModal(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] md:text-xs font-bold text-slate-500 dark:text-slate-400 hover:text-[#0f7699] dark:hover:text-[#4fc3f7] hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              <History size={14} />
              <span>Logs</span>
            </button>
          </div>
        </footer>
        )}
      </main>
      </div>{/* End Main Layout Container */}

      {/* Category Bottom Sheet - Mobile */}
      {showCategorySheet && (
        <div className="fixed inset-0 z-[200] md:hidden" onClick={() => setShowCategorySheet(false)}>
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm animate-in fade-in duration-200" />
          <div className="absolute bottom-0 left-0 right-0 bg-white dark:bg-slate-900 rounded-t-3xl shadow-2xl animate-in slide-in-from-bottom duration-300 max-h-[70vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 bg-slate-300 dark:bg-slate-700 rounded-full" />
            </div>
            <div className="px-5 pb-3 pt-1">
              <h3 className="text-base font-bold text-slate-800 dark:text-slate-100">{t("upload.chooseCategory")}</h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{t("upload.chooseCategoryDesc")}</p>
            </div>
            <div className="flex-1 overflow-y-auto px-3 pb-6">
              {categories.map(c => {
                const Icon = CATEGORY_AVATARS[c.value] || Box;
                const isSelected = selectedCategory === c.value;
                return (
                  <button
                    key={c.value}
                    onClick={() => { setSelectedCategory(c.value); setShowCategorySheet(false); }}
                    className={`w-full flex items-center gap-4 px-4 py-3.5 rounded-2xl mb-1 transition-all active:scale-[0.98] ${
                      isSelected
                        ? "bg-[#0f7699]/10 dark:bg-[#4fc3f7]/15 text-[#0f7699] dark:text-[#4fc3f7]"
                        : "text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      isSelected
                        ? "bg-[#0f7699]/15 dark:bg-[#4fc3f7]/20"
                        : "bg-slate-100 dark:bg-slate-800"
                    }`}>
                      <Icon size={20} />
                    </div>
                    <div className="flex-1 text-left">
                      <span className="text-sm font-bold block">{t(c.labelKey)}</span>
                      <span className="text-[10px] text-slate-400">{c.frames} Frames</span>
                    </div>
                    {isSelected && <Check size={18} className="text-[#0f7699] dark:text-[#4fc3f7]" />}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Logs Modal - Admin only */}
      {showLogsModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-12 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="w-full max-w-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl flex flex-col max-h-[80vh] overflow-hidden shadow-2xl">
            <div className="p-6 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-[#0f7699] dark:text-[#4fc3f7]">{t("upload.serverLogs")}</h2>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Verbindungs- und Verarbeitungslogs ({serverLogs.length} Einträge)</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    const logContent = serverLogs.map(log => `[${log.time}] [${log.type?.toUpperCase() || 'INFO'}] ${log.message}`).join('\n');
                    const jobId = statusInfo.jobId || statusInfo.pythonJobId || 'unknown';
                    const blob = new Blob([`Job: ${jobId}\nDatum: ${new Date().toLocaleString()}\n\n${logContent}`], { type: 'text/plain' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `logs_${jobId}_${new Date().toISOString().split('T')[0]}.txt`;
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                  disabled={serverLogs.length === 0}
                  className="w-10 h-10 bg-slate-100 dark:bg-slate-800 rounded-lg flex items-center justify-center text-slate-500 hover:text-[#0f7699] dark:hover:text-[#4fc3f7] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Logs herunterladen"
                >
                  <Download size={18} />
                </button>
                <button onClick={() => setShowLogsModal(false)} className="w-10 h-10 bg-slate-100 dark:bg-slate-800 rounded-lg flex items-center justify-center text-slate-500 hover:text-slate-800 dark:hover:text-white transition-colors">
                  <XCircle size={20} />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-2 font-mono text-sm">
              {serverLogs.map((log, i) => (
                <div key={i} className={`p-3 rounded-lg border ${
                  log.type === "error" ? "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-700 dark:text-red-400" :
                  log.type === "success" ? "bg-[#0f7699]/5 dark:bg-[#4fc3f7]/10 border-[#0f7699]/20 dark:border-[#4fc3f7]/20 text-[#0f7699] dark:text-[#4fc3f7]" :
                  log.type === "warning" ? "bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-400" :
                  "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-400"
                }`}>
                  <span className="opacity-50 mr-3">[{log.time}]</span>
                  <span>{log.message}</span>
                </div>
              ))}
              {serverLogs.length === 0 && (
                <div className="flex flex-col items-center justify-center py-16 text-slate-400 dark:text-slate-600">
                  <Database size={40} className="mb-4 opacity-30" />
                  <p className="text-sm">{t("upload.noLogs")}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast.open && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[10000] animate-in slide-in-from-bottom-4 duration-300">
          <div className={`px-5 py-3 rounded-xl shadow-lg flex items-center gap-3 ${
            toast.severity === "error" ? "bg-red-500 text-white" :
            toast.severity === "warning" ? "bg-amber-500 text-white" :
            toast.severity === "success" ? "bg-[#0f7699] text-white" :
            "bg-blue-500 text-white"
          }`}>
            <Info size={18} />
            <span className="text-sm font-medium">{toast.message}</span>
            <button onClick={handleCloseToast} className="ml-2 hover:opacity-70">
              <XCircle size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Download Format Modal */}
      {isDownloadModalOpen && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-6 bg-slate-950/60 backdrop-blur-md animate-in fade-in duration-300">
          <div className="w-full max-w-lg bg-white dark:bg-slate-900 rounded-[2.5rem] shadow-2xl overflow-hidden border border-white dark:border-slate-700 animate-in zoom-in-95 duration-300">
            <div className="p-8 border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/50 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#0f7699] dark:bg-[#4fc3f7] rounded-2xl flex items-center justify-center text-white shadow-lg">
                  <Download size={24} />
                </div>
                <h2 className="text-xl font-bold text-[#0f7699] dark:text-[#4fc3f7]">{t("upload.chooseDownloadFormat")}</h2>
              </div>
              <button onClick={() => setIsDownloadModalOpen(false)} className="w-10 h-10 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl flex items-center justify-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors">
                <X size={20} />
              </button>
            </div>

            <div className="p-10 space-y-8">
              <div className="space-y-4">
                {["OBJ+PNG", "STL", "3MF", "ALL"].map(format => (
                  <div
                    key={format}
                    onClick={() => setDownloadFormat(format)}
                    className={`p-6 rounded-3xl border-2 transition-all cursor-pointer flex items-center gap-6 group ${
                      downloadFormat === format
                        ? "border-[#0f7699] dark:border-[#4fc3f7] bg-[#0f7699]/5 dark:bg-[#4fc3f7]/10"
                        : "border-slate-100 dark:border-slate-700 hover:border-[#0f7699]/50 dark:hover:border-[#4fc3f7]/50 hover:bg-slate-50 dark:hover:bg-slate-800"
                    }`}
                  >
                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all ${
                      downloadFormat === format
                        ? "bg-[#0f7699] dark:bg-[#4fc3f7] text-white"
                        : "bg-slate-100 dark:bg-slate-800 text-slate-400 group-hover:bg-[#0f7699]/10 dark:group-hover:bg-[#4fc3f7]/15 group-hover:text-[#0f7699] dark:group-hover:text-[#4fc3f7]"
                    }`}>
                      <FileDownload size={24} />
                    </div>
                    <div className="flex-1">
                      <h3 className={`text-lg font-bold transition-colors ${downloadFormat === format ? "text-[#0f7699] dark:text-[#4fc3f7]" : "text-slate-800 dark:text-slate-200"}`}>
                        {format === "ALL" ? t("upload.allFormats") : format}
                      </h3>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                        {format === "OBJ+PNG" && t("upload.formatObjDesc")}
                        {format === "STL" && t("upload.formatStlDesc")}
                        {format === "3MF" && t("upload.format3mfDesc")}
                        {format === "ALL" && t("upload.formatAllDesc")}
                      </p>
                    </div>
                    <div className={`w-6 h-6 rounded-full border-4 transition-all ${
                      downloadFormat === format ? "border-[#0f7699] dark:border-[#4fc3f7] bg-white dark:bg-slate-900" : "border-slate-200 dark:border-slate-600"
                    }`} />
                  </div>
                ))}
              </div>

              <div className="flex gap-4">
                <button
                  onClick={() => setIsDownloadModalOpen(false)}
                  className="flex-1 h-14 border-2 border-slate-100 dark:border-slate-700 rounded-2xl text-slate-500 dark:text-slate-400 font-bold uppercase tracking-widest text-[11px] hover:bg-slate-50 dark:hover:bg-slate-800 transition-all"
                >
                  {t("upload.cancel")}
                </button>
                <button
                  onClick={handleDownloadConfirm}
                  className="flex-1 h-14 bg-[#0f7699] dark:bg-[#4fc3f7] text-white rounded-2xl font-bold uppercase tracking-widest text-[11px] shadow-xl shadow-[#0f7699]/20 dark:shadow-[#4fc3f7]/20 hover:bg-[#0f7699]/90 dark:hover:bg-[#4fc3f7]/90 transition-all"
                >
                  {t("upload.exportModel")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Feedback Modal */}
      {showFeedbackModal && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center sm:p-4 md:p-12 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="w-full max-w-lg max-h-[90vh] bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-t-2xl sm:rounded-2xl overflow-y-auto shadow-2xl">
            <div className="sticky top-0 z-10 p-5 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between bg-white dark:bg-slate-900">
              <div>
                <h2 className="text-base font-semibold text-[#0f7699] dark:text-[#4fc3f7]">{t("upload.giveFeedback")}</h2>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{t("upload.helpUsImprove")}</p>
              </div>
              <button
                onClick={() => setShowFeedbackModal(false)}
                className="w-8 h-8 bg-slate-100 dark:bg-slate-800 rounded-lg flex items-center justify-center text-slate-500 hover:text-slate-800 dark:hover:text-white transition-colors"
              >
                <XCircle size={16} />
              </button>
            </div>
            <div className="p-5">
              <FeedbackForm
                folder={statusInfo.jobId || statusInfo.pythonJobId || "rejected-scan"}
                loggedInUser={currentUser}
                onClose={handleFeedbackClose}
                onSubmitSuccess={handleFeedbackSubmitted}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
